import { useEffect, useState } from 'react';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { AlertTriangleIcon } from 'lucide-react';

import type { DashboardAggregate } from '@/lib/api';
import { DECILE_BINS, MIN_ANALYSIS_N } from '@/lib/analysis';
import { useDimensionLabel, useNumber, useT, useTn } from '@/lib/i18n';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig,
} from '@/components/ui/chart';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

/**
 * Spread, beside the mean it qualifies (`plan-preproduction` M2).
 *
 * The plan's sentence is the whole brief: *"a dashboard that cannot show a
 * bimodal dimension will report its mean as if it described someone."* The
 * aggregate has always carried `average`; it now carries `distribution` beside
 * it, and this is what draws it.
 *
 * ### Three things this component refuses to smooth over
 *
 * **1. `sufficient: false` withholds the SHAPE, never the COUNT.** The server
 * omits `mean`, `sd`, `min`, `max` and `deciles` below the floor rather than
 * sending numbers it cannot defend, so there is nothing here to render as zero.
 * What is rendered instead is the count and how far it is from the floor — a
 * histogram of eleven candidates is a picture of eleven people, and it looks
 * exactly as authoritative as a picture of eleven hundred.
 *
 * **2. `distribution.n < n` is surfaced, not hidden and not corrected.** They
 * count different populations on purpose: `average`'s `n` counts every result
 * including superseded ones, and `distribution.n` counts candidates. A gap
 * therefore means this cohort has been re-scored and the mean double-counts —
 * a known open defect in `average`. Quietly substituting the honest number would
 * make the dashboard disagree with itself between deploys; quietly hiding the
 * gap would leave a wrong mean unexplained. So it is stated.
 *
 * **3. The floor is named.** M1's payload publishes `minimumN`; the aggregate
 * does not, so `MIN_ANALYSIS_N` is mirrored in `lib/analysis.ts` and held to the
 * contract by a test. Saying "12 of the 30 needed" is a different message from
 * "not enough data", because only one of them tells an operator when to look
 * again.
 *
 * ### Colour (dataviz skill, step 2)
 *
 * SINGLE SERIES — one measure (candidates) across ten ordered bins of one
 * dimension — so no categorical palette applies and no adjacent-pair CVD check
 * has anything to compare. One hue, `--chart-1`, taken from the design system so
 * the histogram re-skins with the console's presets and dark mode. Deliberately
 * NOT a value-ramp across the bins: darker-where-taller would double-encode bar
 * height as hue and burn the only free channel on information the bars already
 * carry.
 *
 * The chart is never the only carrier: the ten bins are also a table, which is
 * the console's precedent (`TableViz`) and the skill's accessibility rule.
 */

type DimensionRow = DashboardAggregate['byDimension'][number];

/** `[0,10)`, …, `[90,100]` — the bins `DECILE_BINS` fixes, labelled. */
function binBounds(index: number): { from: number; to: number } {
  const width = 100 / DECILE_BINS;
  return { from: Math.round(index * width), to: Math.round((index + 1) * width) };
}

export function DimensionSpread({ rows }: { rows: DimensionRow[] }) {
  const t = useT();
  const tn = useTn();
  const number = useNumber();
  const dimensionLabel = useDimensionLabel();

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  /**
   * Follow the data when the module filter changes.
   *
   * Without this, switching module leaves a dimension key selected that the new
   * module does not have, and the card silently falls back to its first
   * dimension while the picker still shows the old name — the picker and the
   * chart would then disagree, which is worse than a reset.
   */
  useEffect(() => {
    if (selectedKey && rows.some((r) => r.key === selectedKey)) return;
    setSelectedKey(rows[0]?.key ?? null);
  }, [rows, selectedKey]);

  const selected = rows.find((r) => r.key === selectedKey) ?? rows[0];

  const config = {
    candidates: { label: t('distribution.bar'), color: 'var(--chart-1)' },
  } satisfies ChartConfig;

  const distribution = selected?.distribution;
  const deciles = distribution?.deciles;
  const bins = (deciles ?? []).map((count, i) => {
    const { from, to } = binBounds(i);
    return {
      band: t('distribution.bin', { from: number(from), to: number(to) }),
      candidates: count,
    };
  });

  return (
    <Card>
      <CardHeader className="gap-2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base">{t('distribution.title')}</CardTitle>
            <CardDescription>{t('distribution.hint')}</CardDescription>
          </div>
          {rows.length > 0 && (
            <Select value={selected?.key ?? ''} onValueChange={setSelectedKey}>
              <SelectTrigger className="w-52" aria-label={t('distribution.pick')}>
                <SelectValue placeholder={t('distribution.pick')} />
              </SelectTrigger>
              <SelectContent>
                {rows.map((row) => (
                  <SelectItem key={row.key} value={row.key}>{dimensionLabel(row.key)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {rows.length === 0 && (
          <p className="text-muted-foreground text-sm">{t('distribution.none')}</p>
        )}

        {/* An API that predates M2 sends no `distribution` at all. That is a
            different fact from "too few candidates", and saying so is how an
            operator knows to look at the deployment rather than at the cohort. */}
        {selected && !distribution && (
          <p className="text-muted-foreground text-sm">{t('distribution.missing')}</p>
        )}

        {selected && distribution && (
          <>
            {/* The re-score signal, first, because it qualifies every number
                below it. Never hidden and never "fixed" on the client. */}
            {distribution.n < selected.n && (
              <Alert>
                <AlertTriangleIcon />
                <AlertTitle>{t('distribution.rescored.title')}</AlertTitle>
                <AlertDescription>
                  {t('distribution.rescored.body', {
                    results: number(selected.n),
                    candidates: number(distribution.n),
                  })}
                </AlertDescription>
              </Alert>
            )}

            {/* The count is stated whether or not the shape is. `n` is never
                withheld — only what is estimated FROM it. */}
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
              <div>
                <dt className="text-muted-foreground text-xs">{t('distribution.column.candidates')}</dt>
                <dd className="font-medium">{tn('distribution.candidates', distribution.n)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">{t('distribution.mean')}</dt>
                <dd className="font-medium">
                  {distribution.mean === undefined ? '—' : number(distribution.mean)}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs" title={t('distribution.sd.hint')}>
                  {t('distribution.sd')}
                </dt>
                <dd className="font-medium">
                  {distribution.sd === undefined ? '—' : number(distribution.sd)}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">{t('distribution.range')}</dt>
                <dd className="font-medium">
                  {distribution.min === undefined || distribution.max === undefined
                    ? '—'
                    : t('distribution.rangeValue', {
                        min: number(distribution.min),
                        max: number(distribution.max),
                      })}
                </dd>
              </div>
            </dl>

            {!distribution.sufficient ? (
              <Alert>
                <AlertTitle>{t('distribution.insufficient.title')}</AlertTitle>
                <AlertDescription>
                  {t('distribution.insufficient.body', {
                    n: number(distribution.n),
                    minimum: number(MIN_ANALYSIS_N),
                  })}
                </AlertDescription>
              </Alert>
            ) : (
              <>
                <ChartContainer config={config} className="max-h-56 w-full">
                  {/* Physical margins deliberately: `ChartContainer` pins the
                      figure to `ltr` in both languages, so the plot's own
                      coordinate space never mirrors. */}
                  <BarChart data={bins} margin={{ left: 8, right: 16, top: 8 }}>
                    <CartesianGrid vertical={false} stroke="var(--border)" />
                    <XAxis
                      dataKey="band"
                      tickLine={false}
                      axisLine={false}
                      interval={0}
                      tick={{ fill: 'var(--muted-foreground)', fontSize: 10 }}
                    />
                    <YAxis
                      allowDecimals={false}
                      width={32}
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }}
                    />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    {/* 4px rounded data-end, anchored to the baseline. */}
                    <Bar dataKey="candidates" fill="var(--color-candidates)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ChartContainer>

                {/* The accessible twin. Every value the chart draws is readable
                    here without a hover, which is the rule for every chart in
                    this console. */}
                <div className="overflow-x-auto">
                  <Table>
                    <caption className="text-muted-foreground caption-bottom pt-2 text-xs">
                      {t('distribution.histogram.title')}
                    </caption>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('distribution.column.band')}</TableHead>
                        <TableHead className="w-28 text-end">
                          {t('distribution.column.candidates')}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bins.map((bin) => (
                        <TableRow key={bin.band}>
                          <TableCell>{bin.band}</TableCell>
                          <TableCell className="text-end font-medium tabular-nums">
                            {number(bin.candidates)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
